import { makeAutoObservable, observable } from 'mobx';

import { i18n } from 'core/localization';
import LocalEventsManager from 'core/localEventsManager';
import { apiFetch } from 'framework/api';
import { ApplicationState } from 'framework/applicationState';
import { ModelValidator } from 'framework/mobx-integration';
import { NavigationStore } from 'framework/navigationStore';
import { Permission } from 'framework/permission';
import { getResponsibleTeamsLite } from 'areas/administration/teams/api';
import { Team } from 'areas/administration/teams/team';
import ModalWindow from 'controls/modalWindow';
import TeamForm from 'areas/administration/teamForm';
import { ServicesRouter } from 'areas/services/bundleDescription';
import { ServiceDesignerRouter } from 'areas/services/designer/bundleDescription';
import { createService, getAccountResponsibleTeam, searchServicesByName } from '../../api';
import { CreateServiceModel, LegacyTeamFormConstructor, TeamSavedEvent } from '../../types';

const LegacyTeamForm = TeamForm as unknown as LegacyTeamFormConstructor;

export class CreateServiceFormStore {
	model = observable<CreateServiceModel>({
		name: '',
		description: '',
		teamIds: [],
		information: '',
		tags: [],
		timeZone: ApplicationState.timezone,
	});
	validator = new ModelValidator(this.model);

	accountId = ApplicationState.accountId;
	teams: Team[] = [];
	loading = true;
	saving = false;
	teamFormOpened = false;

	constructor() {
		makeAutoObservable(this);

		this.validator
			.required('name')
			.add('name', {
				message: i18n('Name exists'),
				callbackAsync: () => this.isNameUnique(),
			})
			.required('teamIds');

		LocalEventsManager.bind('teamSaved', this.onTeamSaved);

		void this.load();
	}

	get okDisabled() {
		return this.loading || this.saving || !this.validator.valid;
	}

	get canCreateTeam() {
		return ApplicationState.hasPermissions(Permission.TeamCreate);
	}

	load = async () => {
		const [teamsResult, accountResult] = await Promise.all([
			apiFetch(getResponsibleTeamsLite({ accountId: this.accountId })),
			apiFetch(getAccountResponsibleTeam(this.accountId)),
		]);

		this.teams = teamsResult.success ? teamsResult.data : [];

		const defaultTeamId = accountResult.success ? accountResult.data.responsibleTeamId : null;
		const defaultTeam = this.teams.find((x) => x.id == defaultTeamId) ?? this.teams[0];
		if (defaultTeam) {
			this.model.teamIds = [defaultTeam.id];
		}

		this.loading = false;
	};

	isNameUnique = async () => {
		const name = this.model.name.trim();
		if (!name) {
			return true;
		}

		const result = await apiFetch(searchServicesByName({ accountId: this.accountId, name }));
		return !result.success || !result.data.items.length;
	};

	openTeamForm = () => {
		this.teamFormOpened = true;
		new ModalWindow({
			title: i18n('Create team'),
			width: 400,
			minHeight: 515,
			height: 'auto',
			url: 'include/Administration/TeamForm.jsp',
			onClose: () => {
				this.teamFormOpened = false;
			},
			refresh: () => {
				new LegacyTeamForm({
					id: null,
					mode: 'create',
				});
			},
		}).open();
	};

	onTeamSaved = async (e: TeamSavedEvent) => {
		const teamId = e.result.data.id;
		const result = await apiFetch(getResponsibleTeamsLite({ accountId: this.accountId }));
		if (!result.success) {
			return;
		}

		this.teams = result.data;
		if (this.teams.some((x) => x.id == teamId) && !this.model.teamIds.includes(teamId)) {
			this.model.teamIds = [...this.model.teamIds, teamId];
		}
	};

	save = async () => {
		this.validator.validate();
		if (!this.validator.valid) {
			return null;
		}

		this.saving = true;
		try {
			const result = await apiFetch(
				createService({
					accountId: this.accountId,
					payload: {
						name: this.model.name.trim(),
						description: this.model.description.trim() || null,
						information: this.model.information.trim(),
						teamIds: this.model.teamIds,
						tags: this.model.tags,
						shared: false,
						operatingSchedule: {
							timeZone: this.model.timeZone,
						},
					},
				}),
			);
			return result.success ? result.data.id : null;
		} finally {
			this.saving = false;
		}
	};

	openCreatedService(serviceId: string) {
		if (ApplicationState.hasPermissions(Permission.ServiceModelCreate)) {
			NavigationStore.go(ServiceDesignerRouter.root(serviceId));
		} else {
			NavigationStore.go(ServicesRouter.details(serviceId));
		}
	}

	destroy() {
		LocalEventsManager.unbind('teamSaved', this.onTeamSaved);
		this.validator.destroy();
	}
}
